"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Gallery = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const HideScroll_1 = require("../HideScroll");
const StyledGallery = app_1.styled.div `
  width: 100%;
  height: 100%;
`;
const StyledElementsWrapper = app_1.styled.div `
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
`;
const StyledDots = app_1.styled.div `
  padding-top: 2rem;
  > button {
    all: unset;
    display: inline-block;
    cursor: pointer;
    margin: 10px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: gray;
    opacity: 0.6;
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
    &:hover {
      opacity: 0.9;
    }
  }
`;
const StyledArrows = app_1.styled.div `
  @media ${app_1.notDesktop} {
    display: none;
  }
  display: flex;
  position: relative;
  padding-bottom: 2rem;
`;
const StyledArrow = app_1.styled.div `
  cursor: pointer;
  opacity: 0.7;
  top: 50%;
  margin-right: 2rem;
`;
const StyledLeftArrow = (0, app_1.styled)(StyledArrow) `
  border-right: 20px solid grey;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
`;
const StyledRightArrow = (0, app_1.styled)(StyledArrow) `
  border-left: 20px solid grey;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
`;
const activeDotCss = (0, app_1.css) `
  opacity: 0.9 !important;
`;
const DEFAULT_CONFIG = {
    '': { elemsPerPage: 3, padding: 0, gap: 0 },
    [app_1.notDesktop]: { elemsPerPage: 2, padding: 0, gap: 0 },
    [app_1.mobile]: { elemsPerPage: 1, padding: 0, gap: 0 },
};
const withRem = (v) => /^\d+$/u.test(v.toString()) ? `${v}rem` : v.toString();
const getCss = ({ mediaConfig = DEFAULT_CONFIG, elements, }) => {
    return Object.keys(mediaConfig).map((bp) => {
        const { padding, elemsPerPage: pageCount, gap } = mediaConfig[bp];
        const gapValue = withRem(gap);
        const [top, right = top, , left = right] = padding
            .toString()
            .split('calc')
            .map((p) => { var _a; return (_a = p.match(/(^\(.+\)|\S+)/gu)) !== null && _a !== void 0 ? _a : []; })
            .reduce((a, b) => [...a, ...b])
            .map(withRem);
        const paddingSum = left === right ? (0, utils_1.multiplyCssValue)(2, left) : `(${left} + ${right})`;
        return (0, app_1.css) `
      ${bp ? `@media ${bp} {` : ''}
      ${(0, app_1.css)({
            padding: padding.toString(),
            width: `calc((100% - ${paddingSum} - ${Math.ceil(pageCount - 1)} * ${gapValue}) * ${elements.length} / ${pageCount} + ${elements.length - 1} * ${gapValue} + ${paddingSum})`,
            gap,
        })}
      ${bp ? '}' : ''}
    `;
    });
};
function scrollJump(scrollRef, containerRef, direction = 'next') {
    var _a;
    if (!scrollRef.current || !containerRef.current) {
        return;
    }
    const { clientWidth, scrollLeft } = scrollRef.current;
    const { paddingLeft, paddingRight } = window.getComputedStyle(containerRef.current);
    const scroll = scrollLeft + Number.parseInt(paddingLeft);
    const children = [...containerRef.current.children];
    const firstVisible = children.findIndex((el) => el.offsetLeft >= scroll - 1);
    const lastVisible = children.length -
        1 -
        [...children]
            .reverse()
            .findIndex((el) => el.offsetLeft + el.clientWidth <=
            scrollLeft + clientWidth - Number.parseInt(paddingRight) + 1);
    const newFirst = direction === 'next'
        ? children[lastVisible + 1] || (0, utils_1.last)(children)
        : (_a = children.find((el) => {
            const prevEl = children[firstVisible - 1] || children[0];
            return (prevEl.offsetLeft + prevEl.clientWidth - el.offsetLeft - 1 <=
                clientWidth);
        })) !== null && _a !== void 0 ? _a : children[0];
    const left = Math.ceil(newFirst.offsetLeft - Number.parseInt(paddingLeft));
    scrollRef.current.scroll({
        behavior: 'smooth',
        left: left === 1 ? 0 : left,
    });
}
function Gallery(_a) {
    var { renderStep: Steps, renderArrows: Arrows, className } = _a, props = __rest(_a, ["renderStep", "renderArrows", "className"]);
    const containerRef = (0, react_1.useRef)(null);
    const scrollRef = (0, react_1.useRef)(null);
    const [activeNext, setActiveNext] = (0, react_1.useState)(true);
    const [activePrev, setActivePrev] = (0, react_1.useState)(false);
    const [steps, setSteps] = (0, react_1.useState)([0]);
    const [activeStep, setActiveStep] = (0, react_1.useState)(0);
    const stepLock = (0, react_1.useRef)(false);
    const targetScroll = (0, react_1.useRef)(0);
    const { elements } = props;
    const countSteps = (0, react_1.useCallback)(() => {
        if (!scrollRef.current || !containerRef.current) {
            return;
        }
        const scrollEl = scrollRef.current;
        const { paddingLeft, paddingRight } = window.getComputedStyle(containerRef.current);
        const clientWidth = scrollRef.current.clientWidth -
            Number.parseInt(paddingLeft) -
            Number.parseInt(paddingRight);
        const children = [...containerRef.current.children];
        const newSteps = [0];
        let stepW = 0;
        let prevElOffset = children[0].offsetLeft;
        children.forEach((el) => {
            const w = el.clientWidth;
            const offset = el.offsetLeft;
            if (stepW + w + offset - prevElOffset >= clientWidth + 1) {
                newSteps.push(Math.min(offset - Number.parseInt(paddingLeft), scrollEl.scrollWidth - scrollEl.clientWidth));
                stepW = w;
            }
            else {
                stepW += offset - prevElOffset + w;
            }
            prevElOffset = offset + w + 1;
        });
        setSteps(newSteps);
    }, []);
    const setStep = (0, react_1.useCallback)((index) => {
        var _a;
        const left = steps[index] || steps[0];
        setActiveStep(index);
        stepLock.current = true;
        targetScroll.current = left;
        (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.scroll({
            behavior: 'smooth',
            left,
        });
    }, [steps]);
    const onScroll = (0, react_1.useCallback)((ev) => {
        var _a, _b;
        const { scrollLeft, clientWidth } = ev.currentTarget;
        const containerWidth = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0;
        setActivePrev(scrollLeft !== 0);
        setActiveNext(scrollLeft !== containerWidth - clientWidth);
        if (!stepLock.current) {
            const nextStep = steps.findIndex((step) => step >= scrollLeft);
            const prevStep = Math.max(0, nextStep - 1);
            setActiveStep((steps[nextStep] || (0, utils_1.last)(steps)) - scrollLeft >
                scrollLeft - (steps[prevStep] || steps[0])
                ? prevStep
                : nextStep);
        }
        else if (scrollLeft === targetScroll.current) {
            stepLock.current = false;
        }
    }, [steps]);
    const { width } = (0, app_1.useWindowSize)();
    (0, react_1.useEffect)(() => countSteps(), [countSteps, width]);
    const previous = activePrev
        ? () => scrollJump(scrollRef, containerRef, 'prev')
        : undefined;
    const next = activeNext
        ? () => scrollJump(scrollRef, containerRef)
        : undefined;
    return ((0, jsx_runtime_1.jsxs)(StyledGallery, Object.assign({ className: className }, { children: [Arrows ? ((0, jsx_runtime_1.jsx)(Arrows, Object.assign({}, { previous, next }))) : ((0, jsx_runtime_1.jsxs)(StyledArrows, { children: [(0, jsx_runtime_1.jsx)(StyledLeftArrow, { onClick: previous }), (0, jsx_runtime_1.jsx)(StyledRightArrow, { onClick: next })] })), (0, jsx_runtime_1.jsx)(HideScroll_1.HideScroll, Object.assign({ ref: scrollRef, onScroll: onScroll }, { children: (0, jsx_runtime_1.jsx)(StyledElementsWrapper, Object.assign({ ref: containerRef, css: getCss(props) }, { children: elements })) })), Steps ? ((0, jsx_runtime_1.jsx)(Steps, { step: activeStep, length: steps.length, setActiveStep: setStep })) : ((0, jsx_runtime_1.jsx)(StyledDots, { children: steps.map((scroll, i) => ((0, jsx_runtime_1.jsx)("button", { onClick: () => setStep(i), css: (0, app_1.css) `
                ${activeStep === i && activeDotCss}
              `, type: "button", "aria-label": `select slide number ${i}` }, scroll))) }))] })));
}
exports.Gallery = Gallery;
